import { useEffect, useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { SOA_OPTIONS, SOA_DEFAULT } from '../../utils/soa';

const TIPI_PRATICA = ['Iscrizione', 'Revisione', 'Rinnovo', 'Variazione'];

const imponibile = (imp) => imp != null ? Number(imp) / 1.22 : 0;
const provvigione = (imp) => imponibile(imp) * 0.5;
const fmt = (n) => n != null ? Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtData = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const oggi = () => new Date().toISOString().slice(0, 10);

// ── Tab Pagamenti ──────────────────────────────────────────────────────────
function TabInsoluti() {
  const [dataRiepilogo, setDataRiepilogo] = useState(oggi());
  const [insoluti, setInsoluti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selezionati, setSelezionati] = useState([]);

  useEffect(() => { carica(); }, [dataRiepilogo]);

  async function carica() {
    setLoading(true);
    try {
      const r = await axios.get('/api/pagamenti/insoluti', { params: { data: dataRiepilogo } });
      setInsoluti(r.data);
      setSelezionati(r.data.map(s => s.id));
    } finally { setLoading(false); }
  }

  function toggle(id) {
    setSelezionati(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function toggleTutti() {
    setSelezionati(prev => prev.length === insoluti.length ? [] : insoluti.map(s => s.id));
  }

  const righeSelezionate = insoluti.filter(s => selezionati.includes(s.id));

  function generaPDF() {
    const doc = new jsPDF();
    const dataStr = new Date(dataRiepilogo + 'T00:00:00').toLocaleDateString('it-IT');
    let primaPagina = true;

    // Raggruppa per valore SOA effettivo nei dati (non filtra per SOA_OPTIONS)
    const soaMap = new Map();
    for (const s of righeSelezionate) {
      const soa = s.pratica.soa || '—';
      if (!soaMap.has(soa)) soaMap.set(soa, []);
      soaMap.get(soa).push(s);
    }

    for (const [soa, righe] of soaMap) {

      if (!primaPagina) doc.addPage();
      primaPagina = false;

      const totImp = righe.reduce((acc, s) => acc + imponibile(s.importo), 0);
      const totProv = righe.reduce((acc, s) => acc + provvigione(s.importo), 0);

      doc.setFontSize(14);
      doc.setTextColor(26, 58, 92);
      doc.setFont(undefined, 'bold');
      doc.text(`Riepilogo al ${dataStr}`, 14, 20);

      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(soa, 14, 30);

      doc.autoTable({
        startY: 38,
        head: [['Impresa', 'Tipo pratica', 'Scadenza', 'Imponibile €', 'Provv. 50% €']],
        body: righe.map(s => [
          s.pratica.impresa.ragione_sociale,
          s.pratica.tipo || '—',
          fmtData(s.data_scadenza),
          fmt(imponibile(s.importo)),
          fmt(provvigione(s.importo)),
        ]),
        foot: [
          ['', '', 'Totale imponibile', fmt(totImp), fmt(totProv)],
          ['', '', 'IVA 22% su provvigioni', '', fmt(totProv * 0.22)],
          ['', '', 'Totale provvigioni c/IVA', '', fmt(totProv * 1.22)],
        ],
        showFoot: 'lastPage',
        headStyles: { fillColor: [26, 58, 92], textColor: 255, fontStyle: 'bold' },
        columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' } },
        didParseCell(data) {
          if (data.section !== 'foot') return;
          if (data.column.index >= 3) data.cell.styles.halign = 'right';
          if (data.row.index === 0) {
            data.cell.styles.fillColor = [243, 244, 246];
            data.cell.styles.textColor = [55, 65, 81];
            data.cell.styles.fontStyle = 'bold';
          } else if (data.row.index === 1) {
            data.cell.styles.fillColor = [238, 244, 251];
            data.cell.styles.textColor = [26, 58, 92];
            data.cell.styles.fontStyle = 'bold';
          } else if (data.row.index === 2) {
            data.cell.styles.fillColor = [26, 58, 92];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
    }

    doc.save(`riepilogo_${dataRiepilogo}.pdf`);
  }

  return (
    <div className="bg-white rounded-xl shadow p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-bold text-[#1A3A5C] text-base">Pagamenti</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Data riepilogo</label>
            <input type="date" value={dataRiepilogo} onChange={e => setDataRiepilogo(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]" />
          </div>
          {righeSelezionate.length > 0 && (
            <button onClick={generaPDF}
              className="bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition">
              ↓ Scarica PDF ({righeSelezionate.length})
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Caricamento...</p>
      ) : insoluti.length === 0 ? (
        <p className="text-gray-500 text-sm">Nessun pagamento.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1A3A5C] text-white text-xs">
              <tr>
                <th className="px-3 py-2 text-center">
                  <input type="checkbox"
                    checked={selezionati.length === insoluti.length && insoluti.length > 0}
                    onChange={toggleTutti}
                    className="cursor-pointer" />
                </th>
                <th className="px-4 py-2 text-left">Impresa</th>
                <th className="px-4 py-2 text-left">SOA</th>
                <th className="px-4 py-2 text-left">Tipo pratica</th>
                <th className="px-4 py-2 text-left">Scadenza</th>
                <th className="px-4 py-2 text-right">Importo c/IVA €</th>
                <th className="px-4 py-2 text-right">Imponibile €</th>
                <th className="px-4 py-2 text-right">Provv. 50% €</th>
              </tr>
            </thead>
            <tbody>
              {insoluti.map((s, i) => {
                const sel = selezionati.includes(s.id);
                return (
                  <tr key={s.id}
                    className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${!sel ? 'opacity-40' : ''} cursor-pointer`}
                    onClick={() => toggle(s.id)}>
                    <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={sel} onChange={() => toggle(s.id)} className="cursor-pointer" />
                    </td>
                    <td className="px-4 py-2 font-medium text-[#1A3A5C]">{s.pratica.impresa.ragione_sociale}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{s.pratica.soa}</td>
                    <td className="px-4 py-2 text-xs text-gray-600">{s.pratica.tipo || '—'}</td>
                    <td className="px-4 py-2 text-gray-700">{fmtData(s.data_scadenza)}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{fmt(s.importo)}</td>
                    <td className="px-4 py-2 text-right font-semibold">{fmt(imponibile(s.importo))}</td>
                    <td className="px-4 py-2 text-right font-semibold text-[#2E6096]">{fmt(provvigione(s.importo))}</td>
                  </tr>
                );
              })}
            </tbody>
            {righeSelezionate.length > 0 && (() => {
              const totImp = righeSelezionate.reduce((acc, s) => acc + imponibile(s.importo), 0);
              const totProv = righeSelezionate.reduce((acc, s) => acc + provvigione(s.importo), 0);
              return (
                <tfoot>
                  <tr className="bg-gray-100 font-semibold text-sm">
                    <td colSpan={6} className="px-4 py-2 text-right text-gray-700">Totale selezionati</td>
                    <td className="px-4 py-2 text-right">{fmt(totImp)}</td>
                    <td className="px-4 py-2 text-right text-[#2E6096]">{fmt(totProv)}</td>
                  </tr>
                  <tr className="bg-[#EEF4FB] text-sm font-semibold">
                    <td colSpan={7} className="px-4 py-2 text-right text-gray-700">IVA 22% su provvigioni</td>
                    <td className="px-4 py-2 text-right text-[#2E6096]">{fmt(totProv * 0.22)}</td>
                  </tr>
                  <tr className="bg-[#1A3A5C] text-white font-bold text-sm">
                    <td colSpan={7} className="px-4 py-2 text-right">Totale provvigioni c/IVA</td>
                    <td className="px-4 py-2 text-right">{fmt(totProv * 1.22)}</td>
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab Gestione ───────────────────────────────────────────────────────────
function TabGestione() {
  const [imprese, setImprese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroSoa, setFiltroSoa] = useState('');
  const [aperte, setAperte] = useState({});
  const [pagina, setPagina] = useState(1);
  const PER_PAGINA = 20;

  useEffect(() => { carica(); }, [filtroSoa]);

  async function carica() {
    setLoading(true);
    try {
      const params = filtroSoa ? `?soa=${encodeURIComponent(filtroSoa)}` : '';
      const r = await axios.get(`/api/pagamenti/imprese${params}`);
      setImprese(r.data);
    } finally { setLoading(false); }
  }

  async function aggiungiPratica(impresaId) {
    try {
      const r = await axios.post('/api/pagamenti/pratiche', {
        impresa_id: impresaId, soa: SOA_DEFAULT, tipo: 'Iscrizione',
      });
      setImprese(prev => prev.map(imp =>
        imp.id === impresaId ? { ...imp, pratiche: [...imp.pratiche, { ...r.data, scadenze: [] }] } : imp
      ));
      setAperte(p => ({ ...p, [impresaId]: true }));
    } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  }

  function aggiornaPratica(impresaId, praticaId, campo, valore) {
    setImprese(prev => prev.map(imp =>
      imp.id !== impresaId ? imp : {
        ...imp,
        pratiche: imp.pratiche.map(p => p.id !== praticaId ? p : { ...p, [campo]: valore }),
      }
    ));
  }

  async function salvaPratica(pratica) {
    try {
      await axios.put(`/api/pagamenti/pratiche/${pratica.id}`, pratica);
    } catch (e) { alert(e.response?.data?.error || 'Errore salvataggio'); }
  }

  async function eliminaPratica(impresaId, praticaId) {
    if (!window.confirm('Eliminare questa pratica e tutte le rate?')) return;
    try {
      await axios.delete(`/api/pagamenti/pratiche/${praticaId}`);
      setImprese(prev => prev.map(imp =>
        imp.id !== impresaId ? imp : { ...imp, pratiche: imp.pratiche.filter(p => p.id !== praticaId) }
      ));
    } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  }

  async function aggiungiScadenza(impresaId, praticaId) {
    try {
      const r = await axios.post('/api/pagamenti/scadenze', {
        pratica_id: praticaId, importo: 0, data_scadenza: oggi(),
      });
      setImprese(prev => prev.map(imp =>
        imp.id !== impresaId ? imp : {
          ...imp,
          pratiche: imp.pratiche.map(p =>
            p.id !== praticaId ? p : { ...p, scadenze: [...p.scadenze, r.data] }
          ),
        }
      ));
    } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  }

  function aggiornaScadenza(impresaId, praticaId, scadenzaId, campo, valore) {
    setImprese(prev => prev.map(imp =>
      imp.id !== impresaId ? imp : {
        ...imp,
        pratiche: imp.pratiche.map(p =>
          p.id !== praticaId ? p : {
            ...p,
            scadenze: p.scadenze.map(s => s.id !== scadenzaId ? s : { ...s, [campo]: valore }),
          }
        ),
      }
    ));
  }

  async function salvaScadenza(scadenza) {
    try {
      await axios.put(`/api/pagamenti/scadenze/${scadenza.id}`, scadenza);
    } catch (e) { alert(e.response?.data?.error || 'Errore salvataggio'); }
  }

  async function toggleSaldato(impresaId, praticaId, scadenza) {
    const nuovoValore = !scadenza.saldato;
    const dataSaldo = null;
    try {
      await axios.put(`/api/pagamenti/scadenze/${scadenza.id}`, { saldato: nuovoValore, data_saldo: dataSaldo });
      setImprese(prev => prev.map(imp =>
        imp.id !== impresaId ? imp : {
          ...imp,
          pratiche: imp.pratiche.map(p =>
            p.id !== praticaId ? p : {
              ...p,
              scadenze: p.scadenze.map(s =>
                s.id !== scadenza.id ? s : { ...s, saldato: nuovoValore, data_saldo: dataSaldo }
              ),
            }
          ),
        }
      ));
    } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  }

  async function eliminaScadenza(impresaId, praticaId, scadenzaId) {
    try {
      await axios.delete(`/api/pagamenti/scadenze/${scadenzaId}`);
      setImprese(prev => prev.map(imp =>
        imp.id !== impresaId ? imp : {
          ...imp,
          pratiche: imp.pratiche.map(p =>
            p.id !== praticaId ? p : { ...p, scadenze: p.scadenze.filter(s => s.id !== scadenzaId) }
          ),
        }
      ));
    } catch (e) { alert(e.response?.data?.error || 'Errore'); }
  }

  const impreseFiltrate = imprese.filter(imp =>
    imp.ragione_sociale.toLowerCase().includes(filtroNome.toLowerCase())
  );
  const totPagine = Math.max(1, Math.ceil(impreseFiltrate.length / PER_PAGINA));
  const impresePagina = impreseFiltrate.slice((pagina - 1) * PER_PAGINA, pagina * PER_PAGINA);

  return (
    <div className="bg-white rounded-xl shadow p-5">
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <h2 className="font-bold text-[#1A3A5C] text-base flex-1">Pratiche per impresa</h2>
        <input type="text" placeholder="Cerca impresa..." value={filtroNome}
          onChange={e => { setFiltroNome(e.target.value); setPagina(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096] w-48" />
        <select value={filtroSoa} onChange={e => { setFiltroSoa(e.target.value); setPagina(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]">
          <option value="">Tutte le SOA</option>
          {SOA_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm py-4">Caricamento...</p>
      ) : (
        <>
          <div className="space-y-2">
            {impresePagina.map(imp => (
              <div key={imp.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-[#EEF4FB] cursor-pointer hover:bg-blue-100 transition"
                  onClick={() => setAperte(p => ({ ...p, [imp.id]: !p[imp.id] }))}>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-[#1A3A5C] text-sm">{imp.ragione_sociale}</span>
                    {imp.pratiche.length > 0 && (
                      <span className="text-xs bg-[#1A3A5C] text-white px-2 py-0.5 rounded-full">
                        {imp.pratiche.length} {imp.pratiche.length === 1 ? 'pratica' : 'pratiche'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={e => { e.stopPropagation(); aggiungiPratica(imp.id); }}
                      className="text-xs text-[#2E6096] hover:underline font-medium">
                      + Pratica
                    </button>
                    <span className="text-gray-400 text-sm">{aperte[imp.id] ? '▲' : '▼'}</span>
                  </div>
                </div>

                {aperte[imp.id] && (
                  <div className="divide-y divide-gray-100">
                    {imp.pratiche.length === 0 ? (
                      <p className="text-sm text-gray-400 px-4 py-3">Nessuna pratica — clicca "+ Pratica" per aggiungerne una.</p>
                    ) : imp.pratiche.map(pratica => (
                      <div key={pratica.id} className="px-4 py-4 bg-white">
                        {/* Campi pratica */}
                        <div className="flex flex-wrap gap-3 items-end mb-4">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Tipo pratica</label>
                            <select value={pratica.tipo || ''}
                              onChange={e => {
                                aggiornaPratica(imp.id, pratica.id, 'tipo', e.target.value);
                                salvaPratica({ ...pratica, tipo: e.target.value });
                              }}
                              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2E6096]">
                              <option value="">— Seleziona —</option>
                              {TIPI_PRATICA.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">SOA</label>
                            <select value={pratica.soa || ''}
                              onChange={e => {
                                aggiornaPratica(imp.id, pratica.id, 'soa', e.target.value);
                                salvaPratica({ ...pratica, soa: e.target.value });
                              }}
                              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2E6096]">
                              {SOA_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Note</label>
                            <input type="text" value={pratica.note || ''}
                              onChange={e => aggiornaPratica(imp.id, pratica.id, 'note', e.target.value)}
                              onBlur={() => salvaPratica(pratica)}
                              placeholder="Note opzionali..."
                              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#2E6096] w-48" />
                          </div>
                          <button onClick={() => eliminaPratica(imp.id, pratica.id)}
                            className="text-xs text-red-500 hover:underline ml-auto">
                            Elimina pratica
                          </button>
                        </div>

                        {/* Rate */}
                        <div className="border-t border-gray-100 pt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rate di pagamento</span>
                            <button onClick={() => aggiungiScadenza(imp.id, pratica.id)}
                              className="text-xs text-[#2E6096] hover:underline">
                              + Aggiungi rata
                            </button>
                          </div>

                          {pratica.scadenze.length === 0 ? (
                            <p className="text-xs text-gray-400">Nessuna rata inserita.</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="text-xs w-full">
                                <thead className="text-gray-500">
                                  <tr>
                                    <th className="text-left py-1 pr-3">Scadenza</th>
                                    <th className="text-right py-1 pr-3">Importo c/IVA €</th>
                                    <th className="text-center py-1 pr-3">Stato</th>
                                    <th className="text-left py-1 pr-3">Data saldo</th>
                                    <th className="text-left py-1 pr-3">Note</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pratica.scadenze.map(s => (
                                    <tr key={s.id} className={s.saldato ? 'opacity-50' : ''}>
                                      <td className="py-1 pr-3">
                                        <input type="date" value={s.data_scadenza ? s.data_scadenza.slice(0, 10) : ''}
                                          onChange={e => aggiornaScadenza(imp.id, pratica.id, s.id, 'data_scadenza', e.target.value)}
                                          onBlur={() => salvaScadenza(s)}
                                          className="border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#2E6096]" />
                                      </td>
                                      <td className="py-1 pr-3 text-right">
                                        <input type="number" step="any" value={s.importo ?? ''}
                                          onChange={e => aggiornaScadenza(imp.id, pratica.id, s.id, 'importo', e.target.value)}
                                          onBlur={() => salvaScadenza(s)}
                                          className="border border-gray-200 rounded px-1 py-0.5 text-xs w-24 text-right focus:outline-none focus:ring-1 focus:ring-[#2E6096]" />
                                      </td>
                                      <td className="py-1 pr-3 text-center">
                                        <button onClick={() => toggleSaldato(imp.id, pratica.id, s)}
                                          className={`px-2 py-0.5 rounded-full text-xs font-bold transition ${s.saldato ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                          {s.saldato ? 'Saldato' : 'Da saldare'}
                                        </button>
                                      </td>
                                      <td className="py-1 pr-3">
                                        {s.saldato
                                          ? <input type="date"
                                              defaultValue={s.data_saldo ? s.data_saldo.slice(0, 10) : ''}
                                              onBlur={async e => {
                                                try { await axios.put(`/api/pagamenti/scadenze/${s.id}`, { data_saldo: e.target.value || null }); } catch {}
                                              }}
                                              className="border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#2E6096]" />
                                          : <span className="text-gray-300">—</span>
                                        }
                                      </td>
                                      <td className="py-1 pr-3">
                                        <input type="text" value={s.note || ''}
                                          onChange={e => aggiornaScadenza(imp.id, pratica.id, s.id, 'note', e.target.value)}
                                          onBlur={() => salvaScadenza(s)}
                                          placeholder="Note..."
                                          className="border border-gray-200 rounded px-1 py-0.5 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-[#2E6096]" />
                                      </td>
                                      <td className="py-1">
                                        <button onClick={() => eliminaScadenza(imp.id, pratica.id, s.id)}
                                          className="text-red-400 hover:text-red-600 text-xs">✕</button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {totPagine > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">{impreseFiltrate.length} imprese — pagina {pagina} di {totPagine}</span>
              <div className="flex gap-2">
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1}
                  className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40">← Prec.</button>
                <button onClick={() => setPagina(p => Math.min(totPagine, p + 1))} disabled={pagina === totPagine}
                  className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40">Succ. →</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Componente principale ──────────────────────────────────────────────────
export default function Pagamenti() {
  const [tab, setTab] = useState('insoluti');

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-[#1A3A5C]">Pagamenti pratiche</h1>

      <div className="flex gap-1 border-b border-gray-200">
        {[['insoluti', 'Tutti i pagamenti'], ['gestione', 'Gestione pratiche']].map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${
              tab === val ? 'border-[#1A3A5C] text-[#1A3A5C]' : 'border-transparent text-gray-500 hover:text-[#1A3A5C]'
            }`}>{label}</button>
        ))}
      </div>

      {tab === 'insoluti' && <TabInsoluti />}
      {tab === 'gestione' && <TabGestione />}
    </div>
  );
}
